import { Search, SlidersHorizontal, X } from "lucide-react";
import { BAGHDAD_CITIES, EMPTY_FILTERS, IRAQ_GOVERNORATES, SHOP_CLASSES } from "../constants";
import type { Shop, ShopFilters, ShopSortKey } from "../types";

type FilterBarProps = {
  filters: ShopFilters;
  shops: Shop[];
  onChange: (filters: ShopFilters) => void;
};

const sortOptions: Array<{ value: ShopSortKey; label: string }> = [
  { value: "newest", label: "Newest" },
  { value: "shopName", label: "Shop name" },
  { value: "shopGovenorate", label: "Governorate" },
  { value: "shopCity", label: "City" },
  { value: "shopClass", label: "Class" }
];

function unique(values: Array<string | null>): string[] {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value)))).sort();
}

export function FilterBar({ filters, shops, onChange }: FilterBarProps) {
  const cities = unique([...BAGHDAD_CITIES, ...shops.map((shop) => shop.shopCity)]);
  const districts = unique(shops.map((shop) => shop.shopDistrict));
  const hasFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  const update = <Key extends keyof ShopFilters>(key: Key, value: ShopFilters[Key]) => {
    onChange({
      ...filters,
      [key]: value
    });
  };

  return (
    <section className="filter-bar" aria-label="Search and filters">
      <label className="search-field">
        <Search size={18} />
        <input
          value={filters.search}
          onChange={(event) => update("search", event.target.value)}
          placeholder="Search shops, phone, city, district..."
        />
      </label>

      <div className="filter-grid">
        <label>
          <span>Class</span>
          <select value={filters.shopClass} onChange={(event) => update("shopClass", event.target.value as ShopFilters["shopClass"])}>
            <option value="all">All classes</option>
            {SHOP_CLASSES.map((shopClass) => (
              <option key={shopClass} value={shopClass}>
                Class {shopClass}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Governorate</span>
          <select value={filters.shopGovenorate} onChange={(event) => update("shopGovenorate", event.target.value)}>
            <option value="">All governorates</option>
            {IRAQ_GOVERNORATES.map((governorate) => (
              <option key={governorate} value={governorate}>
                {governorate}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>City</span>
          <select value={filters.shopCity} onChange={(event) => update("shopCity", event.target.value)}>
            <option value="">All cities</option>
            {cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>District</span>
          <select value={filters.shopDistrict} onChange={(event) => update("shopDistrict", event.target.value)}>
            <option value="">All districts</option>
            {districts.map((district) => (
              <option key={district} value={district}>
                {district}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Location</span>
          <select value={filters.locationStatus} onChange={(event) => update("locationStatus", event.target.value as ShopFilters["locationStatus"])}>
            <option value="all">All locations</option>
            <option value="exact">Has location</option>
            <option value="missing">Missing location</option>
            <option value="text_only">Text only</option>
          </select>
        </label>

        <label>
          <span>Sort</span>
          <select value={filters.sortBy} onChange={(event) => update("sortBy", event.target.value as ShopSortKey)}>
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="filter-footer">
        <span>
          <SlidersHorizontal size={16} />
          {shops.length} saved shops
        </span>
        {hasFilters ? (
          <button type="button" className="ghost-button compact-button" onClick={() => onChange(EMPTY_FILTERS)}>
            <X size={16} />
            Clear filters
          </button>
        ) : null}
      </div>
    </section>
  );
}
